import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Slider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';
import { privacyPolicyText, termsOfServiceText, cookiesPolicyText } from '../../data/legalDefaults';

const Settings = () => {
  const [settings, setSettings] = useState({
    general: {
      siteName: 'JobIT Portal',
      siteDescription: 'Your premier destination for career growth and professional opportunities',
      contactEmail: 'admin@jobit.com',
      supportPhone: '+1 (555) 123-4567',
    },
    // Public page content
    about: {
      title: 'About Us',
      subtitle: 'Connecting Talent with Opportunity',
      missionTitle: 'Our Mission',
      missionBody: 'To revolutionize the way people connect with career opportunities by providing an innovative, user-friendly platform that empowers both job seekers and employers to achieve their goals.',
      body: `Welcome to JobIT, your premier destination for career growth and professional opportunities. We're dedicated to bridging the gap between talented individuals and leading employers, creating meaningful connections that drive success.\n\nOur platform serves as a comprehensive ecosystem where job seekers can discover opportunities, employers can find exceptional talent, and service providers can showcase their expertise. We're committed to fostering a community that values innovation, growth, and mutual success.`,
      teamMembers: [
        {
          name: 'John Doe',
          role: 'CEO & Founder',
          image: 'https://source.unsplash.com/random/400x400/?portrait&1',
          description: 'Leading our mission to connect talent with opportunity.',
        },
        {
          name: 'Jane Smith',
          role: 'Head of Operations',
          image: 'https://source.unsplash.com/random/400x400/?portrait&2',
          description: 'Ensuring smooth platform operations and user satisfaction.',
        },
        {
          name: 'Mike Johnson',
          role: 'Tech Lead',
          image: 'https://source.unsplash.com/random/400x400/?portrait&3',
          description: 'Driving innovation in our technical solutions.',
        },
      ],
    },
    contact: {
      heroTitle: 'Contact Us',
      heroSubtitle: "We're Here to Help",
      email: 'contact@jobit.com',
      phone: '+1 (555) 123-4567',
      address: '123 Business Street, Tech City, TC 12345',
    },
    // Legal content for public pages, editable by admin
    legal: {
      privacyPolicy: '',
      termsOfService: '',
      cookiesPolicy: '',
    },
    notifications: {
      emailNotifications: true,
      jobAlerts: true,
      applicationUpdates: true,
      marketingEmails: false,
    },
    security: {
      twoFactorAuth: false,
      passwordExpiry: 90,
      sessionTimeout: 30,
      requireStrongPassword: true,
    },
    localization: {
      defaultLanguage: 'en',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '24h',
    },
    // Admin control for job seeker contact visibility on public pages
    jobSeekerPrivacy: {
      showContactInfo: true,
    },
    boost: {
      enabled: true,
      employerBoostPrice: 49.0,
      jobSeekerBoostPrice: 29.0,
      jobBoostPrice: 19.0,
      defaultDurationDays: 30,
      featuredEmployersLimit: 6,
      featuredJobSeekersLimit: 6,
      featuredJobsLimit: 12,
    },
    payments: {
      enableBoostPayments: true,
      currency: 'USD',
      provider: 'manual',
    },
    // Free job posting settings
    freePosting: {
      enforceLimit: false,
      generalFreeJobQuota: 5,
    },
    // Paid job posting settings
    jobPosting: {
      pricePerPosting: 10.0,
      maxAdditionalPurchases: 30,
    },
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Admin direct password change state
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('');
  const [adminPassError, setAdminPassError] = useState('');
  const [adminPassLoading, setAdminPassLoading] = useState(false);

  // Load admin settings on mount: prefer local cache, then overlay backend
  React.useEffect(() => {
    let mounted = true;

    const safeMerge = (prev, next) => ({
      ...prev,
      ...(next || {}),
      about: {
        ...prev.about,
        ...(next?.about || {}),
      },
      contact: {
        ...prev.contact,
        ...(next?.contact || {}),
      },
      legal: {
        ...prev.legal,
        ...(next?.legal || {}),
      },
      jobSeekerPrivacy: {
        ...prev.jobSeekerPrivacy,
        ...(next?.jobSeekerPrivacy || {}),
      },
      boost: {
        ...prev.boost,
        ...(next?.boost || {}),
      },
      payments: {
        ...prev.payments,
        ...(next?.payments || {}),
      },
      freePosting: {
        ...prev.freePosting,
        ...(next?.freePosting || {}),
      },
      jobPosting: {
        ...prev.jobPosting,
        ...(next?.jobPosting || {}),
      },
    });

    const hasMeaningfulSettings = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      const keys = ['about','contact','legal','jobSeekerPrivacy','boost','payments','freePosting','jobPosting'];
      return keys.some((k) => obj[k] && Object.keys(obj[k] || {}).length > 0);
    };

    const applyLegalDefaults = (obj) => ({
      ...obj,
      legal: {
        privacyPolicy: (obj?.legal?.privacyPolicy || '').trim() || privacyPolicyText,
        termsOfService: (obj?.legal?.termsOfService || '').trim() || termsOfServiceText,
        cookiesPolicy: (obj?.legal?.cookiesPolicy || '').trim() || cookiesPolicyText,
      },
    });

    const load = async () => {
      // 1) Prefer locally cached settings first
      try {
        const rawLocal = localStorage.getItem('adminSettings') || localStorage.getItem('systemSettings');
        if (rawLocal && mounted) {
          const parsedLocal = JSON.parse(rawLocal);
          setSettings((prev) => applyLegalDefaults(safeMerge(prev, parsedLocal)));
        }
      } catch (_) { /* ignore local parse errors */ }

      // 2) Overlay backend settings if available
      try {
        const remote = await ApiService.getSystemSettings();
        if (!mounted) return;
        if (hasMeaningfulSettings(remote)) {
          setSettings((prev) => applyLegalDefaults(safeMerge(prev, remote)));
        } else {
          // Ensure defaults even if backend has no sections yet
          setSettings((prev) => applyLegalDefaults(safeMerge(prev, {})));
        }
      } catch (e) {
        console.warn('Failed to load system settings from backend, using local cache if available:', e);
        // Apply defaults if nothing was set from local
        setSettings((prev) => applyLegalDefaults(prev));
      }
    };

    load();
    return () => { mounted = false; };
  }, []);

  const handleChange = (section, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [field]: value,
      },
    });
  };

  const handleSave = async () => {
    // Always persist locally and broadcast, even if backend is unavailable
    // so the frontend reflects the latest content immediately.
    let saved = null;
    try {
      saved = await ApiService.updateSystemSettings(settings);
    } catch (e) {
      console.warn('Backend update failed, saving locally only:', e);
    }

    // Merge backend response with current form state, ensuring legal/about/contact stay intact
    const toPersist = {
      ...(saved && Object.keys(saved).length ? saved : {}),
      ...settings,
      about: { ...(settings?.about || {}) },
      contact: { ...(settings?.contact || {}) },
      legal: { ...(settings?.legal || {}) },
    };

    try {
      localStorage.setItem('adminSettings', JSON.stringify(toPersist));
      localStorage.setItem('systemSettings', JSON.stringify(toPersist));
    } catch (_) {}

    // Broadcast settings update so all open tabs/pages refresh
    try {
      const event = new CustomEvent('systemSettingsUpdated', { detail: toPersist });
      window.dispatchEvent(event);
    } catch (_) {}

    // Feedback depending on backend status
    if (saved) {
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Saved locally; backend unavailable. Frontend updated.', severity: 'warning' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAdminPasswordChange = async () => {
    setAdminPassError('');
    if (adminNewPassword.length < 8) {
      setAdminPassError('New password must be at least 8 characters.');
      return;
    }
    if (adminNewPassword !== adminConfirmPassword) {
      setAdminPassError('Password confirmation does not match.');
      return;
    }

    try {
      setAdminPassLoading(true);
      const resp = await ApiService.request('/admin-auth/change-password-direct', {
        method: 'PUT',
        body: JSON.stringify({ newPassword: adminNewPassword, confirmPassword: adminConfirmPassword }),
      });
      if (resp?.success) {
        setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
        setAdminNewPassword('');
        setAdminConfirmPassword('');
      } else {
        setAdminPassError(resp?.message || 'Failed to change password');
      }
    } catch (err) {
      setAdminPassError(err?.message || 'Failed to change password');
    } finally {
      setAdminPassLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Platform Settings
      </Typography>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Site Name"
                    value={settings.general.siteName}
                    onChange={handleChange('general', 'siteName')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={settings.general.contactEmail}
                    onChange={handleChange('general', 'contactEmail')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Site Description"
                    value={settings.general.siteDescription}
                    onChange={handleChange('general', 'siteDescription')}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Notification Settings</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.emailNotifications}
                    onChange={handleChange('notifications', 'emailNotifications')}
                  />
                }
                label="Email Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.jobAlerts}
                    onChange={handleChange('notifications', 'jobAlerts')}
                  />
                }
                label="Job Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.applicationUpdates}
                    onChange={handleChange('notifications', 'applicationUpdates')}
                  />
                }
                label="Application Updates"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.marketingEmails}
                    onChange={handleChange('notifications', 'marketingEmails')}
                  />
                }
                label="Marketing Emails"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Security Settings</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.twoFactorAuth}
                    onChange={handleChange('security', 'twoFactorAuth')}
                  />
                }
                label="Two-Factor Authentication"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.requireStrongPassword}
                    onChange={handleChange('security', 'requireStrongPassword')}
                  />
                }
                label="Require Strong Password"
              />
              <TextField
                fullWidth
                label="Password Expiry (days)"
                type="number"
                value={settings.security.passwordExpiry}
                onChange={handleChange('security', 'passwordExpiry')}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Session Timeout (minutes)"
                type="number"
                value={settings.security.sessionTimeout}
                onChange={handleChange('security', 'sessionTimeout')}
                margin="normal"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Change Admin Password (no current password required) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Change Admin Password</Typography>
              </Box>
              {adminPassError && (
                <Alert severity="error" sx={{ mb: 2 }}>{adminPassError}</Alert>
              )}
              <TextField
                label="New Password"
                type="password"
                fullWidth
                value={adminNewPassword}
                onChange={(e) => setAdminNewPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                value={adminConfirmPassword}
                onChange={(e) => setAdminConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleAdminPasswordChange}
                disabled={adminPassLoading}
              >
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Localization Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LanguageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Localization Settings</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Default Language"
                    value={settings.localization.defaultLanguage}
                    onChange={handleChange('localization', 'defaultLanguage')}
                    margin="normal"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Time Format"
                    value={settings.localization.timeFormat}
                    onChange={handleChange('localization', 'timeFormat')}
                    margin="normal"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="12h">12-hour</option>
                    <option value="24h">24-hour</option>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* About & Contact Content */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">About & Contact Content</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure public page text. Leave fields blank to use defaults.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    About Page
                  </Typography>
                  <TextField
                    fullWidth
                    label="About Title"
                    value={settings.about.title}
                    onChange={handleChange('about', 'title')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="About Subtitle"
                    value={settings.about.subtitle}
                    onChange={handleChange('about', 'subtitle')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Mission Title"
                    value={settings.about.missionTitle}
                    onChange={handleChange('about', 'missionTitle')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Mission Body"
                    value={settings.about.missionBody}
                    onChange={handleChange('about', 'missionBody')}
                    margin="normal"
                    multiline
                    minRows={4}
                  />
                  <TextField
                    fullWidth
                    label="About Body"
                    value={settings.about.body}
                    onChange={handleChange('about', 'body')}
                    margin="normal"
                    multiline
                    minRows={6}
                  />
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Team Members
                  </Typography>
                  {(settings.about.teamMembers || []).map((m, idx) => (
                    <Grid container spacing={2} key={`team-${idx}`} sx={{ mb: 1 }}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={m.name || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const next = [...(settings.about.teamMembers || [])];
                            next[idx] = { ...next[idx], name: val };
                            setSettings({
                              ...settings,
                              about: { ...settings.about, teamMembers: next },
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Role"
                          value={m.role || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const next = [...(settings.about.teamMembers || [])];
                            next[idx] = { ...next[idx], role: val };
                            setSettings({
                              ...settings,
                              about: { ...settings.about, teamMembers: next },
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Image URL"
                          value={m.image || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const next = [...(settings.about.teamMembers || [])];
                            next[idx] = { ...next[idx], image: val };
                            setSettings({
                              ...settings,
                              about: { ...settings.about, teamMembers: next },
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={m.description || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const next = [...(settings.about.teamMembers || [])];
                            next[idx] = { ...next[idx], description: val };
                            setSettings({
                              ...settings,
                              about: { ...settings.about, teamMembers: next },
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              const next = (settings.about.teamMembers || []).filter((_, i) => i !== idx);
                              setSettings({
                                ...settings,
                                about: { ...settings.about, teamMembers: next },
                              });
                            }}
                          >
                            Remove Member
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const next = [...(settings.about.teamMembers || [])];
                        next.push({ name: '', role: '', image: '', description: '' });
                        setSettings({
                          ...settings,
                          about: { ...settings.about, teamMembers: next },
                        });
                      }}
                    >
                      Add Member
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Contact Page
                  </Typography>
                  <TextField
                    fullWidth
                    label="Hero Title"
                    value={settings.contact.heroTitle}
                    onChange={handleChange('contact', 'heroTitle')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Hero Subtitle"
                    value={settings.contact.heroSubtitle}
                    onChange={handleChange('contact', 'heroSubtitle')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={settings.contact.email}
                    onChange={handleChange('contact', 'email')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    value={settings.contact.phone}
                    onChange={handleChange('contact', 'phone')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Contact Address"
                    value={settings.contact.address}
                    onChange={handleChange('contact', 'address')}
                    margin="normal"
                    multiline
                    minRows={2}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Seeker Privacy Controls */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Job Seeker Contact Visibility</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Control whether public job seeker profiles show full email and phone numbers.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.jobSeekerPrivacy.showContactInfo}
                    onChange={handleChange('jobSeekerPrivacy', 'showContactInfo')}
                  />
                }
                label={settings.jobSeekerPrivacy.showContactInfo ? 'Show full contact details' : 'Mask contact details'}
              />
              <Divider sx={{ my: 2 }} />
              <Alert severity="info">
                When masked, emails show initials like <strong>ph@*************</strong> and phones like <strong>097*******</strong>, with a "Request for contact" button.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Boost & Payments */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Boost & Payments</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!settings.boost.enabled}
                        onChange={handleChange('boost', 'enabled')}
                      />
                    }
                    label="Enable profile boosting"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!settings.payments.enableBoostPayments}
                        onChange={handleChange('payments', 'enableBoostPayments')}
                      />
                    }
                    label="Require payment for boost"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Employer boost price"
                    type="number"
                    value={settings.boost.employerBoostPrice}
                    onChange={handleChange('boost', 'employerBoostPrice')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job seeker boost price"
                    type="number"
                    value={settings.boost.jobSeekerBoostPrice}
                    onChange={handleChange('boost', 'jobSeekerBoostPrice')}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job boost price"
                    type="number"
                    value={settings.boost.jobBoostPrice}
                    onChange={handleChange('boost', 'jobBoostPrice')}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Boost duration (days)"
                    type="number"
                    value={settings.boost.defaultDurationDays}
                    onChange={handleChange('boost', 'defaultDurationDays')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Currency"
                    value={settings.payments.currency}
                    onChange={handleChange('payments', 'currency')}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Featured employers limit"
                    type="number"
                    value={settings.boost.featuredEmployersLimit}
                    onChange={handleChange('boost', 'featuredEmployersLimit')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Featured job seekers limit"
                    type="number"
                    value={settings.boost.featuredJobSeekersLimit}
                    onChange={handleChange('boost', 'featuredJobSeekersLimit')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Featured jobs limit"
                    type="number"
                    value={settings.boost.featuredJobsLimit}
                    onChange={handleChange('boost', 'featuredJobsLimit')}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Free Job Posting Limits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Free Job Posting Limits</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!settings.freePosting.enforceLimit}
                        onChange={handleChange('freePosting', 'enforceLimit')}
                      />
                    }
                    label="Enforce free posting limit"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="General free job quota"
                    type="number"
                    value={settings.freePosting.generalFreeJobQuota}
                    onChange={handleChange('freePosting', 'generalFreeJobQuota')}
                    margin="normal"
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Per-Employer Quota Management
              </Typography>
              <PerEmployerQuotaManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Job Posting Pricing */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Job Posting Pricing</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Price per posting"
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    value={settings.jobPosting.pricePerPosting}
                    onChange={handleChange('jobPosting', 'pricePerPosting')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max additional purchases"
                    type="number"
                    inputProps={{ min: 1, max: 30 }}
                    value={settings.jobPosting.maxAdditionalPurchases}
                    onChange={handleChange('jobPosting', 'maxAdditionalPurchases')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Cost Preview
                  </Typography>
                  <Box px={2}>
                    <Slider
                      valueLabelDisplay="auto"
                      min={0}
                      max={Number(settings.jobPosting.maxAdditionalPurchases) || 30}
                      value={Math.min(Number(settings.jobPosting.previewCount || 0), Number(settings.jobPosting.maxAdditionalPurchases) || 30)}
                      onChange={(_, val) => {
                        setSettings({
                          ...settings,
                          jobPosting: {
                            ...settings.jobPosting,
                            previewCount: Array.isArray(val) ? val[0] : val
                          }
                        });
                      }}
                    />
                    <Typography variant="body2">
                      {(Number(settings.jobPosting.previewCount || 0))} postings × {(Number(settings.jobPosting.pricePerPosting || 0)).toFixed(2)} = {
                        ((Number(settings.jobPosting.previewCount || 0)) * (Number(settings.jobPosting.pricePerPosting || 0))).toFixed(2)
                      }
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Legal Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Legal Settings</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control text for Privacy Policy, Terms of Service, and Cookies pages. Leave a field blank to use default content.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Privacy Policy"
                    placeholder="Paste your Privacy Policy text here"
                    value={settings.legal.privacyPolicy}
                    onChange={handleChange('legal', 'privacyPolicy')}
                    margin="normal"
                    multiline
                    minRows={8}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Terms of Service"
                    placeholder="Paste your Terms of Service text here"
                    value={settings.legal.termsOfService}
                    onChange={handleChange('legal', 'termsOfService')}
                    margin="normal"
                    multiline
                    minRows={8}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cookies Policy"
                    placeholder="Optional: Paste your Cookies Policy text here"
                    value={settings.legal.cookiesPolicy}
                    onChange={handleChange('legal', 'cookiesPolicy')}
                    margin="normal"
                    multiline
                    minRows={6}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          size="large"
        >
          Save Settings
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;

// Lightweight inline component for managing employer quotas
const PerEmployerQuotaManager = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [employer, setEmployer] = useState(null);
  const [quota, setQuota] = useState('');
  const [resetUsed, setResetUsed] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');

  const findEmployer = async () => {
    setLoading(true);
    setMessage('');
    try {
      const user = await ApiService.adminFindUser({ email });
      if (!user || user.userType !== 'employer') {
        setSeverity('error');
        setMessage('Employer not found or not an employer');
        setEmployer(null);
      } else {
        setEmployer(user);
        setQuota(
          typeof user?.employerProfile?.freeJobQuota === 'number'
            ? String(user.employerProfile.freeJobQuota)
            : ''
        );
        setSeverity('success');
        setMessage('Employer loaded');
      }
    } catch (e) {
      setSeverity('error');
      setMessage(e.message || 'Failed to find employer');
    } finally {
      setLoading(false);
    }
  };

  const saveQuota = async () => {
    if (!employer) return;
    setLoading(true);
    setMessage('');
    try {
      const quotaValue = quota === '' ? null : parseInt(quota, 10);
      const resp = await ApiService.adminSetEmployerQuota(employer.id, {
        freeJobQuota: quotaValue,
        resetUsed,
      });
      setSeverity('success');
      setMessage('Employer quota updated');
      setEmployer({
        ...employer,
        employerProfile: {
          ...employer.employerProfile,
          freeJobQuota: resp?.employer?.freeJobQuota ?? quotaValue,
          freeJobUsed: resp?.employer?.freeJobUsed ?? employer.employerProfile?.freeJobUsed ?? 0,
        },
      });
    } catch (e) {
      setSeverity('error');
      setMessage(e.message || 'Failed to update quota');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Employer email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Button variant="outlined" onClick={findEmployer} disabled={loading || !email}>
            {loading ? 'Searching...' : 'Find Employer'}
          </Button>
        </Grid>
      </Grid>

      {message && (
        <Alert severity={severity} sx={{ mt: 2 }}>{message}</Alert>
      )}

      {employer && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            {employer?.employerProfile?.companyName || 'Unknown Company'} — {employer.email}
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Per-employer free quota (blank to use general)"
                type="number"
                value={quota}
                onChange={(e) => setQuota(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={resetUsed} onChange={(e) => setResetUsed(e.target.checked)} />}
                label="Reset used count"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="contained" onClick={saveQuota} disabled={loading}>
                {loading ? 'Saving...' : 'Save Employer Quota'}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Current used: {employer?.employerProfile?.freeJobUsed ?? 0}
          </Typography>
        </Box>
      )}
    </Box>
  );
};